import startOfWeek from "date-fns/startOfWeek";
import endOfWeek from "date-fns/endOfWeek";
import addDays from "date-fns/addDays";
import createTrend from "trendline";

export const randomBetween = (max, min) => Math.floor(Math.random() * (max - min + 1) + min);

export const sleep = (max = 800, min = 200) =>
    new Promise((r) => {
        setTimeout(r, randomBetween(max, min));
    });

const start = startOfWeek(new Date());

export const days = Array(12)
    .fill(start)
    .map((e, i) => addDays(e, i));

export const getDays = (startDate) =>
    Array(12)
        .fill(startDate)
        .map((e, i) => addDays(e, i));

export const round = (num, decimals = 2) =>
    (Math.round((num + Number.EPSILON) * 10 ** decimals) / 10 ** decimals).toFixed(decimals);

export const average = (array) => {
    const avg = array.reduce((a, b) => a + b) / array.length;
    return round(avg, 3);
};

export const trend = (values) => {
    const data = values.map((y, i) => ({ x: i, y }));
    const trendValues = createTrend(data, "x", "y");
    return round(trendValues.slope, 3);
};

const MEAN = 4;
const STD = (8 - 0) / 4;

const getZ = (value, mean = MEAN, std = STD) => (value - mean) / std;

function GetZPercent(z) {
    // z == number of standard deviations from the mean

    // if z is greater than 6.5 standard deviations from the mean the
    // number of significant digits will be outside of a reasonable range

    if (z < -6.5) {
        return 0.0;
    }

    if (z > 6.5) {
        return 1.0;
    }

    let factK = 1;
    let sum = 0;
    let term = 1;
    let k = 0;
    const loopStop = Math.exp(-23);

    while (Math.abs(term) > loopStop) {
        term =
            (((0.3989422804 * (-1) ** k * z ** k) / (2 * k + 1) / 2 ** k) * z ** (k + 1)) / factK;
        sum += term;
        // eslint-disable-next-line no-plusplus
        k++;
        factK *= k;
    }

    sum += 0.5;

    return sum;
}

export const getPercentage = (value) => {
    const percentageValue = GetZPercent(getZ(value));
    return round((1 - percentageValue) * 100, 0);
};

const generateRandomValue = (max, min) => Math.random() * (max - min) + min;

export const generateRandomValues = (max, min, decimals = 3) =>
    Array(12)
        .fill(0)
        .map(() => generateRandomValue(max, min))
        .map((num) =>
            parseFloat(
                (Math.round((num + Number.EPSILON) * 10 ** decimals) / 10 ** decimals).toFixed(
                    decimals
                )
            )
        );

export const getIndex = (values, organisation) => ({
    value: values[values.length - 1],
    trend: {
        personal: trend(values) * 10,
        organisation,
    },
});

export const getBenchmark = (averageValue) => 1 - getPercentage(averageValue * 10) / 100;

export const getData = (values, daysArray) => {
    if (daysArray.length !== values.length) throw new Error("Wrong data");
    return daysArray.map((day, index) => ({
        date: day.valueOf() / 1000,
        value: values[index],
    }));
};

export const isDateRangeInThisWeek = (fromDate, toDate) => {
    const today = new Date();
    today.setHours(0, 0, 0);
    const startDate = startOfWeek(today, { weekStartsOn: 1 });
    const endDate = endOfWeek(today, { weekStartsOn: 1 });
    if (startDate.valueOf() > fromDate || endDate.valueOf() < toDate) return false;
    return true;
};

const generateRandomValueFixed = (max, min) =>
    parseFloat((Math.random() * (max - min) + min).toFixed(2));

const splitNParts = (value, parts) => {
    const sumParts = [];
    // eslint-disable-next-line no-plusplus
    for (let i = 0; i < parts - 1; i++) {
        const sum = sumParts.reduce((acc, curr) => acc + curr, 0);
        const pn = Math.min(
            generateRandomValueFixed(
                (value * 10) / parts,
                Math.min(value * 10 - Math.max(sum, (value * 10) / 2), 10)
            ),
            10
        );
        sumParts.push(pn);
    }
    sumParts.push(Math.min(value * 10 - sumParts.reduce((acc, curr) => acc + curr, 0), 10));
    return sumParts.map((part) => part / 10);
};

const generateMetrics = (containers) => (values) => {
    const splitValues = splitNParts(
        values[values.length - 1] * containers.length,
        containers.length
    );
    const splitValuesOld = splitNParts(
        values[values.length - 2] * containers.length,
        containers.length
    );
    return containers.map((container, index) => ({
        ...container,
        value: splitValues[index],
        oldValue: splitValuesOld[index],
        trend: (splitValues[index] - splitValuesOld[index]) / splitValuesOld[index],
    }));
};

export const constructMockData =
    ({ defaultValues, defaultMetrics, max, min, metrics, valueGetter, metricsContainers }) =>
    ({ fromDate, toDate }) => {
        let values;
        const daysArray = getDays(startOfWeek(fromDate || new Date(), { weekStartsOn: 1 }));
        if (valueGetter) {
            values = valueGetter({ fromDate, toDate });
        } else if (!fromDate || !toDate || isDateRangeInThisWeek(fromDate, toDate)) {
            values = defaultValues;
        } else {
            values = generateRandomValues(max, min);
        }

        const metricValues = metricsContainers
            ? generateMetrics(metricsContainers)(values)
            : metrics || defaultMetrics;

        const averageValue = average(values);
        const index = getIndex(values, -0.03);
        const benchmarkObject = {
            benchmark: getBenchmark(averageValue),
        };
        return {
            timeseries: getData(values, daysArray),
            benchmarkObject,
            index,
            metrics: metricValues,
        };
    };

export const constructMockOverviewData =
    ({ defaultValues, max, min, metrics }) =>
    ({ fromDate, toDate }) => {
        let values;
        const names = Object.keys(defaultValues[0]);
        const daysArray = getDays(startOfWeek(fromDate || new Date(), { weekStartsOn: 1 }));
        if (!fromDate || !toDate || isDateRangeInThisWeek(fromDate, toDate)) {
            values = defaultValues.map((value, index) => ({
                ...value,
                date: daysArray[index].valueOf() / 1000,
            }));
        } else {
            values = defaultValues.map((_value, index) => ({
                ...names.reduce(
                    (acc, name) => ({ ...acc, [name]: generateRandomValue(max, min) }),
                    {}
                ),
                date: daysArray[index].valueOf() / 1000,
            }));
        }

        const benchmarkObject = {};
        const indexObject = {};
        let benchmarkValue = 0;
        let indexValue = 0;

        names.forEach((name) => {
            const onlyValues = values.map((value) => value[name]);
            const averageValue = average(onlyValues);
            const index = averageValue;
            const benchmark = getBenchmark(averageValue);
            benchmarkObject[name] = benchmark;
            indexObject[name] = index;
            benchmarkValue += +benchmark;
            indexValue += +index;
        });
        benchmarkObject.overall = benchmarkValue / names.length;
        indexObject.overall = indexValue / names.length;

        return {
            timeseries: values,
            benchmarkObject,
            index: indexObject,
            metrics,
        };
    };
